import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconEdit, IconTrash } from '../components/icons';
import { Modal } from '../components/Modal';
import { useAuth } from '../auth/useAuth';
import { useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { formatMoney } from '../utils/format';

interface Line {
  id: number;
  member_id: number;
  member: { id: number; user: { first_name: string; last_name: string } | null };
  gross_wages: string;
  employee_rate: string;
  employer_rate: string;
  employee_amount: string;
  employer_amount: string;
}

interface ScheduleDetail {
  id: number;
  employer_id: number;
  employer: { id: number; name: string };
  period_start: string;
  period_end: string;
  status: 'draft' | 'submitted' | 'receipted';
  paid_to_date: string;
  receipt_number: string | null;
  total_due: number;
  outstanding: number;
  lines: Line[];
}

interface MemberOption {
  id: number;
  user: { first_name: string; last_name: string } | null;
}

interface ImportRowError {
  row: number;
  email: string | null;
  errors: string[];
}

function formatPeriod(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  const s = new Date(start).toLocaleDateString('en-GB', opts);
  const e = new Date(end).toLocaleDateString('en-GB', { ...opts, year: 'numeric' });
  return `${s} – ${e}`;
}

const STATUS_BADGE: Record<ScheduleDetail['status'], string> = {
  draft: 'badge-scheduled',
  submitted: 'badge-neutral',
  receipted: 'badge-active',
};

function memberName(member: Line['member']): string {
  return member.user ? `${member.user.first_name} ${member.user.last_name}` : `Member #${member.id}`;
}

export function ContributionScheduleDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { settings } = useConfig();
  const confirmAction = useConfirm();
  const { showError, showSuccess } = useToast();

  const [schedule, setSchedule] = useState<ScheduleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [members, setMembers] = useState<MemberOption[]>([]);

  const [addingLine, setAddingLine] = useState(false);
  const [lineForm, setLineForm] = useState({ member_id: '', gross_wages: '', employee_rate: '', employer_rate: '' });
  const [editingLine, setEditingLine] = useState<Line | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<ImportRowError[]>([]);

  const [recordingPayment, setRecordingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ payment_method: '', amount: '' });

  const canManageLines = can('memberships.contributions.manage') || can('employer.contributions.manage');
  // Manual line entry needs a member picker sourced from GET /members, which
  // is gated by memberships.view|member.profile.view — a self-service
  // employer has neither (by design: they shouldn't be able to browse the
  // whole member registry), so they add lines via CSV import only, matching
  // members by email from their own payroll data instead.
  const canAddLinesManually = can('memberships.contributions.manage');
  const canReceipt = can('memberships.contributions.manage');
  const isDraft = schedule?.status === 'draft';
  const isSubmitted = schedule?.status === 'submitted';

  const fmtMoney = (n: number | string) => formatMoney(Number(n), settings.currency);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get<ScheduleDetail>(`/contribution-schedules/${id}`);
      setSchedule(res.data);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load contribution schedule.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!canAddLinesManually) return;
    api
      .get<{ data: MemberOption[] }>('/members', { params: { per_page: 100 } })
      .then((res) => setMembers(res.data.data))
      .catch(() => setMembers([]));
  }, [canAddLinesManually]);

  const openAddLine = () => {
    setLineForm({
      member_id: '',
      gross_wages: '',
      employee_rate: String(settings.min_employee_contribution_rate),
      employer_rate: String(settings.min_employer_contribution_rate),
    });
    setErrors({});
    setAddingLine(true);
  };

  const submitAddLine = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/contribution-schedules/${id}/lines`, {
        member_id: Number(lineForm.member_id),
        gross_wages: Number(lineForm.gross_wages),
        employee_rate: Number(lineForm.employee_rate),
        employer_rate: Number(lineForm.employer_rate),
      });
      setAddingLine(false);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const openEditLine = (line: Line) => {
    setEditingLine(line);
    setLineForm({
      member_id: String(line.member_id),
      gross_wages: line.gross_wages,
      employee_rate: line.employee_rate,
      employer_rate: line.employer_rate,
    });
    setErrors({});
  };

  const submitEditLine = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingLine) return;
    try {
      await api.put(`/contribution-schedule-lines/${editingLine.id}`, {
        member_id: Number(lineForm.member_id),
        gross_wages: Number(lineForm.gross_wages),
        employee_rate: Number(lineForm.employee_rate),
        employer_rate: Number(lineForm.employer_rate),
      });
      setEditingLine(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const removeLine = async (line: Line) => {
    if (!(await confirmAction(`Remove ${memberName(line.member)} from this schedule?`))) return;
    try {
      await api.delete(`/contribution-schedule-lines/${line.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const submitSchedule = async () => {
    if (!schedule) return;
    if (!(await confirmAction('Submit this schedule to VNPF for receipting? Lines can no longer be edited afterwards.', { confirmLabel: 'Submit' })))
      return;
    try {
      await api.post(`/contribution-schedules/${schedule.id}/submit`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Submit failed.');
    }
  };

  const removeSchedule = async () => {
    if (!schedule) return;
    if (!(await confirmAction('Delete this draft schedule?'))) return;
    try {
      await api.delete(`/contribution-schedules/${schedule.id}`);
      navigate('/memberships/contribution-schedules');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const onImportFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    setImportFile(e.target.files?.[0] ?? null);
    setImportErrors([]);
  };

  const submitImport = async () => {
    if (!schedule || !importFile) return;
    const form = new FormData();
    form.append('file', importFile);
    try {
      const res = await api.post<{ imported: number }>(`/contribution-schedules/${schedule.id}/import`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showSuccess(`Imported ${res.data.imported} line(s).`);
      setImporting(false);
      setImportFile(null);
      setImportErrors([]);
      load();
    } catch (err) {
      if (err instanceof ApiError && Array.isArray(err.errors)) {
        setImportErrors(err.errors as unknown as ImportRowError[]);
      } else {
        showError(err instanceof ApiError ? err.message : 'Import failed.');
      }
    }
  };

  const openRecordPayment = () => {
    setPaymentForm({ payment_method: '', amount: schedule ? String(schedule.outstanding) : '' });
    setErrors({});
    setRecordingPayment(true);
  };

  const submitPayment = async (e: FormEvent) => {
    e.preventDefault();
    if (!schedule) return;
    try {
      const res = await api.post<ScheduleDetail>(`/contribution-schedules/${schedule.id}/receipt`, {
        payment_method: paymentForm.payment_method,
        amount: Number(paymentForm.amount),
      });
      setRecordingPayment(false);
      showSuccess(
        res.data.status === 'receipted' ? `Fully paid — receipt ${res.data.receipt_number}.` : 'Partial payment recorded.'
      );
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] });
    }
  };

  if (loading) return null;
  if (error || !schedule) {
    return (
      <>
        <header className="page-header">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/memberships/contribution-schedules')} />
        </header>
        <div className="card">
          <p className="error">{error ?? 'Contribution schedule not found.'}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/memberships/contribution-schedules')} />
          <h1>
            {schedule.employer.name}: {formatPeriod(schedule.period_start, schedule.period_end)}
          </h1>
        </div>
        <div className="page-header-actions">
          <span className={`badge ${STATUS_BADGE[schedule.status]}`}>{schedule.status}</span>
          {canManageLines && isDraft && schedule.lines.length > 0 && (
            <button type="button" className="btn btn-primary" onClick={submitSchedule}>
              Submit
            </button>
          )}
          {canReceipt && isSubmitted && (
            <button type="button" className="btn btn-primary" onClick={openRecordPayment}>
              Record payment
            </button>
          )}
          {canManageLines && isDraft && (
            <button type="button" className="btn btn-ghost" onClick={removeSchedule}>
              Delete
            </button>
          )}
        </div>
      </header>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="form-grid">
          <div>
            <strong>Total due</strong>
            <p>{fmtMoney(schedule.total_due)}</p>
          </div>
          <div>
            <strong>Paid to date</strong>
            <p>{fmtMoney(schedule.paid_to_date)}</p>
          </div>
          <div>
            <strong>Outstanding</strong>
            <p>{fmtMoney(schedule.outstanding)}</p>
          </div>
          {schedule.receipt_number && (
            <div>
              <strong>Receipt number</strong>
              <p>{schedule.receipt_number}</p>
            </div>
          )}
        </div>
      </div>

      {canManageLines && isDraft && (
        <div className="toolbar">
          {canAddLinesManually && (
            <button type="button" className="btn btn-primary" onClick={openAddLine}>
              + Add member
            </button>
          )}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setImportFile(null);
              setImportErrors([]);
              setImporting(true);
            }}
          >
            Import CSV
          </button>
        </div>
      )}

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Member</th>
                <th>Gross Wages</th>
                <th>Employee Rate</th>
                <th>Employee Amount</th>
                <th>Employer Rate</th>
                <th>Employer Amount</th>
                {canAddLinesManually && isDraft && <th></th>}
              </tr>
            </thead>
            <tbody>
              {schedule.lines.length === 0 ? (
                <tr>
                  <td colSpan={canAddLinesManually && isDraft ? 7 : 6} className="muted center">
                    No members on this schedule yet.
                  </td>
                </tr>
              ) : (
                schedule.lines.map((line) => (
                  <tr key={line.id}>
                    <td>
                      <strong>{memberName(line.member)}</strong>
                    </td>
                    <td>{fmtMoney(line.gross_wages)}</td>
                    <td>{line.employee_rate}%</td>
                    <td>{fmtMoney(line.employee_amount)}</td>
                    <td>{line.employer_rate}%</td>
                    <td>{fmtMoney(line.employer_amount)}</td>
                    {canAddLinesManually && isDraft && (
                      <td className="right">
                        <IconButton icon={IconEdit} label="Edit" onClick={() => openEditLine(line)} />
                        <IconButton icon={IconTrash} label="Remove" variant="danger-ghost" onClick={() => removeLine(line)} />
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(addingLine || editingLine) && (
        <Modal title={editingLine ? 'Edit member line' : 'Add member'} onClose={() => (editingLine ? setEditingLine(null) : setAddingLine(false))}>
          <form onSubmit={editingLine ? submitEditLine : submitAddLine} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Member *
              <select
                value={lineForm.member_id}
                onChange={(e) => setLineForm({ ...lineForm, member_id: e.target.value })}
                required
              >
                <option value="">Select…</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {memberName(m)}
                  </option>
                ))}
              </select>
              {errors.member_id && <small className="error">{errors.member_id[0]}</small>}
            </label>
            <label className="span-2">
              Gross wages *
              <input
                type="number"
                step="0.01"
                min="0"
                value={lineForm.gross_wages}
                onChange={(e) => setLineForm({ ...lineForm, gross_wages: e.target.value })}
                required
              />
              {errors.gross_wages && <small className="error">{errors.gross_wages[0]}</small>}
            </label>
            <label>
              Employee rate (%) *
              <input
                type="number"
                step="0.01"
                min={settings.min_employee_contribution_rate}
                value={lineForm.employee_rate}
                onChange={(e) => setLineForm({ ...lineForm, employee_rate: e.target.value })}
                required
              />
              {errors.employee_rate && <small className="error">{errors.employee_rate[0]}</small>}
            </label>
            <label>
              Employer rate (%) *
              <input
                type="number"
                step="0.01"
                min={settings.min_employer_contribution_rate}
                value={lineForm.employer_rate}
                onChange={(e) => setLineForm({ ...lineForm, employer_rate: e.target.value })}
                required
              />
              {errors.employer_rate && <small className="error">{errors.employer_rate[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => (editingLine ? setEditingLine(null) : setAddingLine(false))}
              >
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}

      {importing && (
        <Modal title="Import members from CSV" onClose={() => setImporting(false)}>
          <p className="muted">
            Columns: <code>email, gross_wages, employee_rate, employer_rate</code>. If any row fails validation,
            nothing is imported.
          </p>
          <input type="file" accept=".csv,text/csv" onChange={onImportFileSelected} />
          {importErrors.length > 0 && (
            <div className="alert alert-error" style={{ marginTop: 12 }}>
              <strong>{importErrors.length} row(s) failed validation — nothing was imported:</strong>
              <ul>
                {importErrors.map((e) => (
                  <li key={e.row}>
                    Row {e.row} ({e.email ?? 'no email'}): {e.errors.join(' ')}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setImporting(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" disabled={!importFile} onClick={submitImport}>
              Import
            </button>
          </div>
        </Modal>
      )}

      {recordingPayment && (
        <Modal title="Record payment" onClose={() => setRecordingPayment(false)}>
          <form onSubmit={submitPayment} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <p className="muted span-2">Outstanding balance: {fmtMoney(schedule.outstanding)}</p>
            <label className="span-2">
              Payment method *
              <input
                value={paymentForm.payment_method}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                required
              />
              {errors.payment_method && <small className="error">{errors.payment_method[0]}</small>}
            </label>
            <label className="span-2">
              Amount *
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={schedule.outstanding}
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                required
              />
              {errors.amount && <small className="error">{errors.amount[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setRecordingPayment(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
